import _ from 'lodash'
import cx from 'classnames'
import T from 'prop-types'
import React from 'react'
import ScrollBars from './scrollbars/ScrollBars'
import LoadingValue from './LoadingValue'
import FileUploadProgress from './FileUploadProgress'
import { formatBytes, pluralize } from 'pw-formatters'

const DEFAULT_MAX_SINGLE_FILE_SIZE_BYTES = 8e6 // 8MB

// const FILE_ITEM_HEIGHT = 46
// const MAX_VISIBLE_FILES = 7

export default class FileUploadReader extends React.Component {
  static displayName = 'FileUploadReader'

  static propTypes = {
    allowedFileExtensions: T.array, // array of file extensions
    className: T.string,
    disabled: T.bool,
    fileParser: T.func.isRequired,
    maxFileSize: T.number,
    onChange: T.func.isRequired,
    children: T.node,
    openOnMount: T.bool,
    simpleLoadingIndicator: T.bool
  }

  static defaultProps = {
    allowedFileExtensions: ['.txt'],
    className: '',
    disabled: false,
    maxFileSize: DEFAULT_MAX_SINGLE_FILE_SIZE_BYTES
  }

  componentDidMount() {
    if (this.props.openOnMount && this._fileInput) {
      this._fileInput.click()
    }
  }


  _getInitialState = () => {
    return {
      files: [], // array of Files chosen by the user
      parsedFiles: {}, // Map of fileName:fileTextContents
      fileProgress: {},
      selectedFileNames: [] // Currently selected fileNames
    }
  }

  _onFileClick = file => {
    this.setState({
      selectedFileNames: _.xor(this.state.selectedFileNames, [file.name])
    })
  }

  _handleFiles = e => {
    const numFiles = e.target.files.length
    const files = new Array(numFiles)
    for (var i = 0; i < numFiles; i++) {
      files[i] = e.target.files[i]
    }
    this.setState(
      {
        files,
        selectedFileNames: _.map(files, file => file.name)
      },
      this._startLoadingFiles
    )
  }

  _updateFileProgress = (fileName, partialProgress) => {
    this.setState({
      fileProgress: _.mapValues(this.state.fileProgress, (progress, name) => {
        return name === fileName
          ? _.assign(progress, partialProgress)
          : progress
      })
    })
  }

  _handleFileError = (fileName, error) => {
    console.warn('IntelRuleUploader: File Read Error --', error, fileName)
    this._updateFileProgress(fileName, {
      loaded: 'File read error'
    })
    this.setState({
      parsedFiles: _.assign(this.state.parsedFiles, _.set({}, fileName, []))
    })
  }

  _handleFileProgress = (fileName, progress) => {
    this._updateFileProgress(fileName, {
      loaded: progress.loaded,
      total: progress.total
    })
  }

  _handleFileLoaded = (fileName, resultEvent) => {
    const { fileParser, maxFileSize } = this.props
    if (resultEvent.loaded > maxFileSize) {
      this._updateFileProgress(fileName, {
        error: `${formatBytes(
          resultEvent.loaded,
          true
        )} Exceeds maximum file size of ${formatBytes(maxFileSize)}`
      })
    } else {
      // File loaded successfully. Pass contents to supplied parser
      const textResult = resultEvent.target.result
      const _newEntry = {}
      _newEntry[fileName] = fileParser(textResult)
      _newEntry[fileName].fileSize = resultEvent.loaded
      this.setState(
        {
          parsedFiles: _.assign(this.state.parsedFiles, _newEntry)
        },
        this._checkFinished
      )
    }
  }

  _checkFinished = () => {
    const { fileProgress } = this.state
    const isFinished =
      _.size(fileProgress) > 0 &&
      _.every(fileProgress, p => p.total === p.loaded)
    if (isFinished) {
      this._emitChange()
    }
  }

  _startLoadingFiles = () => {
    const { files, selectedFileNames } = this.state

    const _newFileProgress = {}

    for (var i = 0; i < selectedFileNames.length; i++) {
      const fileName = selectedFileNames[i]
      const file = _.find(files, { name: fileName })

      const reader = new FileReader()
      reader.onload = _.partial(this._handleFileLoaded, fileName)
      reader.onerror = _.partial(this._handleFileError, fileName)
      reader.onprogress = _.partial(this._handleFileProgress, fileName)
      reader.readAsText(file)

      _newFileProgress[file.name] = {
        loaded: 0,
        total: -1,
        error: null
      }
    }

    this.setState({
      fileProgress: _newFileProgress,
      parsedFiles: {} // Reset
    })
  }

  _emitChange = () => {
    const { onChange } = this.props
    const { parsedFiles } = this.state
    onChange(parsedFiles)
    this._reset()
  }

  _handleUploadButtonClick = () => {
    this._fileInput.click()
  }

  _reset = () => {
    this.setState(this._getInitialState())
  }

  _bindFileInputRef = el => {
    this._fileInput = el
  }

  state = this._getInitialState()

  render() {
    const { props, state } = this
    const {
      allowedFileExtensions,
      className,
      disabled,
      children,
      simpleLoadingIndicator
    } = props
    const {
      files,
      fileProgress,
      // parsedFiles,
      selectedFileNames
    } = state

    const fileCount = _.size(fileProgress)
    const isLoading = fileCount > 0 && _.some(fileProgress, p => p.total !== p.loaded)
    // const isLoaded = fileCount > 0 && _.every(fileProgress, p => p.total === p.loaded)

    const classes = cx(`file_upload_reader btn btn-default`, className, {
      disabled: disabled,
      loading: isLoading,
      // loaded: isLoaded,
      inactive: !isLoading // "basic" initial button view
    })

    if (isLoading) {
      if (simpleLoadingIndicator) {
        return (
          <div className={classes}>
            <LoadingValue light /> Loading {files.length} {pluralize(files.length, 'file')}
          </div>
        )
      }
      return (
        <div className={classes}>
          <header>
            Loading {files.length} {pluralize(files.length, 'file')}
          </header>
          <div className="file_menu_scrolled_list">
            <ScrollBars classes="pw_scrollbars_light" slimShady slimShadyDarker>
              {_.map(files, file => {
                const isSelected = selectedFileNames.indexOf(file.name) !== -1
                return (
                  <FileUploadProgress
                    file={file}
                    key={file.name}
                    onClick={null}
                    progress={
                      fileProgress ? fileProgress[file.name] : undefined
                    }
                    selected={isSelected}
                  />
                )
              })}
            </ScrollBars>
          </div>
          <footer>
            <div>
              <LoadingValue light /> Reading Files
            </div>
            <button className={`btn btn-link`} onClick={this._reset}>
              Cancel
            </button>
          </footer>
        </div>
      )
    }
    // { isLoading ? (
    //   <span><LoadingValue light /> Reading Files</span>
    // ) : (
    //   <span>
    //     <span className="icon icon-checkmark" /> Import Loaded { pluralize(files.length, 'Rule') }
    //   </span>
    // ) }

    return (
      <div
        className={classes}
        data-tooltip={`Load files...`}
        disabled={disabled}
        onClick={disabled ? null : this._handleUploadButtonClick}
      >
        <input
          accept={allowedFileExtensions.join(',')}
          className="super_hidden_input"
          multiple
          onChange={this._handleFiles}
          ref={this._bindFileInputRef}
          type="file"
        />
        <span className={`icon icon-upload`} />
        {children}
        {/* ><span className="icon icon-upload"/> { files.length > 0 ? `${ files.length } ${ pluralize(files.length, 'File') } Selected` : 'Upload Files' }</button> */}
      </div>
    )
  }
}
